/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { globalAlert } from "../actions/commonActions";
import LmsQuizResultSummary from "./LmsQuizResultSummary";
import {
  learnerQuizStartApi,
  learnerQuizDraftApi,
  learnerQuizSubmitApi,
} from "./_learnerActions";
import { getCachedPortalInfo, loadPortalInfoCached } from "./learnerPortalInfoCache";
import { learnerPath } from "./_learnerConfig";
import {
  buildQuizAnswersPayload,
  hydrateQuizDraftAnswers,
  resolveQuizEnrolmentId,
} from "./quizAttemptUtils";

// A question allows multiple answers when its type says so (learner payloads
// strip correctness, so we can't infer — default single unless multiple_choice).
const isMulti = (q) => (q?.questionType || "single_choice") === "multiple_choice";

const fmtClock = (s) => {
  const m = Math.floor(Math.max(0, s) / 60);
  const ss = String(Math.max(0, s % 60)).padStart(2, "0");
  return `${m}:${ss}`;
};

const normalizeIds = (value) => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((item) => item?._id || item?.id || item?.optionId || item)
    .filter(Boolean)
    .map(String);
};

const requiresRemainingAnswers = (message = "") =>
  /remaining question|unanswered|answer the remaining|answer.*before submitting/i.test(message);

// ─────────────────────────────────────────────────────────────────────────────
// Dedicated full-screen ASSESSMENT page (own route, own chrome — no portal nav).
// Route: /e-learning/:orgName/course/:courseId/quiz/:quizId  (nav state may carry
// { enrolmentId, title, subtitle, maxAttempts }). Starts/resumes a real attempt,
// one question at a time with Previous/Next + a Question Navigator to jump,
// optional countdown (auto-submit at 0), Submit → server-graded result with
// hints/explanations, and an Exit control.
// ─────────────────────────────────────────────────────────────────────────────
const LearnerQuiz = () => {
  const { t } = useTranslation();
  const { orgName, courseId, quizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = location.state || {};
  const enrolmentId = resolveQuizEnrolmentId(location.search, nav);

  const [questions, setQuestions] = useState([]);
  const [attempt, setAttempt] = useState(null);
  const [passMark, setPassMark] = useState(nav.passMark ?? 0);
  const [maxAttempts, setMaxAttempts] = useState(nav.maxAttempts ?? null);
  const [answers, setAnswers] = useState({}); // { [questionId]: string[] }
  const [idx, setIdx] = useState(0);
  const [visitedQuestions, setVisitedQuestions] = useState(new Set([0]));
  const [remaining, setRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [isFs, setIsFs] = useState(false);
  const [organisation, setOrganisation] = useState(null);
  const [submitWarning, setSubmitWarning] = useState("");
  const [draftState, setDraftState] = useState("saved");

  const timerRef = useRef(null);
  const draftTimerRef = useRef(null);
  const submittingRef = useRef(false);
  const submitRef = useRef(null);
  const attemptRef = useRef(null);
  const answersRef = useRef({});
  const questionsRef = useRef([]);

  const fallbackBrandName = "ESHS Learning";
  const fallbackBrandLogo = "/assets/icons/eC-Card-Logo.svg";
  const cachedOrganisation = getCachedPortalInfo(orgName)?.organisation || null;
  const visibleOrganisation = organisation || cachedOrganisation;
  const brandName = visibleOrganisation?.name || fallbackBrandName;
  const brandLogo = visibleOrganisation?.brandLogoUrl || fallbackBrandLogo;

  const backToCourse = () => navigate(learnerPath(orgName, `course/${courseId}/player`));

  /* ── start / resume the attempt ── */
  useEffect(() => {
    start();
    document.body.classList.add("lms-assess-open");
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      stopTimer();
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      if (attemptRef.current?.attemptId && !submittingRef.current) {
        learnerQuizDraftApi(
          attemptRef.current.attemptId,
          buildQuizAnswersPayload(questionsRef.current, answersRef.current)
        ).catch(() => {});
      }
      document.body.classList.remove("lms-assess-open");
      document.removeEventListener("fullscreenchange", onFs);
    };
  }, [quizId]);

  useEffect(() => {
    attemptRef.current = attempt;
  }, [attempt]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);

  useEffect(() => {
    let active = true;

    const loadOrganisation = async () => {
      if (!orgName) return;
      try {
        const data = await loadPortalInfoCached(orgName);
        if (active) {
          setOrganisation(data?.organisation || null);
        }
      } catch {
        if (active && !cachedOrganisation) setOrganisation(null);
      }
    };

    loadOrganisation();
    return () => {
      active = false;
    };
  }, [orgName]);

  const start = async () => {
    setLoading(true);
    setBlocked("");
    setSubmitWarning("");
    stopTimer();
    try {
      const res = await learnerQuizStartApi(quizId, enrolmentId, courseId);
      const { status, data, message } = res?.data || {};
      if (status === 200) {
        const at = data?.attempt || data || {};
        setAttempt(at);
        setPassMark(Number(at?.passMark) || 0);
        if (at?.maxAttempts != null) setMaxAttempts(at.maxAttempts);
        const qs = (at?.questions || []).map((qq) => ({
          id: qq._id || qq.questionId,
          text: qq.questionText || "",
          multi: isMulti(qq),
          marks: Number(qq.marks) || 1,
          options: (qq.options || []).map((o) => ({ id: o._id, text: o.text })),
        }));
        questionsRef.current = qs;
        setQuestions(qs);
        setIdx(0);
        // Seed saved draft answers on resume.
        const seeded = hydrateQuizDraftAnswers(at?.draftAnswers || []);
        answersRef.current = seeded;
        setAnswers(seeded);
        const visited = new Set([0]);
        qs.forEach((question, questionIndex) => {
          if ((seeded[question.id] || []).length) visited.add(questionIndex);
        });
        setVisitedQuestions(visited);
        setResult(null);
        if (at?.timeLimitMinutes) {
          const started = at?.startedAt ? new Date(at.startedAt).getTime() : Date.now();
          startTimer(started + at.timeLimitMinutes * 60000);
        }
      } else {
        setBlocked(message || t("learner.lms.quiz.unavailable", "This quiz is not available right now."));
      }
    } catch (e) {
      const message = e?.response?.data?.message || t("common.somethingWentWrong");
      setBlocked(message);
      globalAlert("error", message);
    }
    setLoading(false);
  };

  const startTimer = (endsAt) => {
    const tick = () => {
      const left = Math.round((endsAt - Date.now()) / 1000);
      setRemaining(left);
      if (left <= 0) {
        stopTimer();
        if (!submittingRef.current) submitRef.current?.(true);
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
  };
  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  /* ── answers ── */
  const answersPayload = () =>
    buildQuizAnswersPayload(questionsRef.current, answersRef.current);

  const saveDraftNow = async () => {
    const attemptId = attemptRef.current?.attemptId;
    if (!attemptId || result || submittingRef.current) return false;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = null;
    setDraftState("saving");
    try {
      const res = await learnerQuizDraftApi(attemptId, answersPayload());
      const saved = res?.data?.status === 200;
      setDraftState(saved ? "saved" : "error");
      return saved;
    } catch {
      setDraftState("error");
      return false;
    }
  };

  useEffect(() => {
    if (!attempt?.attemptId || result || submittingRef.current) return undefined;
    setDraftState("saving");
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      saveDraftNow();
    }, 1200);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [answers, attempt?.attemptId, result]);

const toggle = (q, optId) => {
  setAnswers((prev) => {
    const cur = new Set(prev[q.id] || []);

    if (q.multi) {
      // Checkbox
      cur.has(optId) ? cur.delete(optId) : cur.add(optId);
    } else {
      // Radio with deselect support
      if (cur.has(optId)) {
        cur.clear(); // Uncheck if already selected
      } else {
        cur.clear();
        cur.add(optId);
      }
    }

    const next = { ...prev, [q.id]: [...cur] };
    answersRef.current = next;
    return next;
  });
  setSubmitWarning("");
};

  /* ── submit ── */
  const submit = async (auto = false) => {
    const completedAt = new Date().toISOString();
    if (submittingRef.current) return;
    const answered = Object.values(answers).filter((a) => a && a.length).length;
    if (!auto && answered < total) {
      setSubmitWarning(
        t(
          "learner.lms.quiz.answerRequiredMessage",
          "Please answer every question before submitting."
        )
      );
      const firstUnanswered = questions.findIndex((question) => !(answers[question.id] || []).length);
      if (firstUnanswered >= 0) goToQuestion(firstUnanswered);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    const secondsLeft = remaining;
    stopTimer();
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = null;
    try {
      const res = await learnerQuizSubmitApi(attemptRef.current?.attemptId, answersPayload(), enrolmentId);
      const { status, data, message } = res?.data || {};
      const r = data?.result;
      if (status === 200 && r) {
        const byId = {};
        questions.forEach((q) => (byId[q.id] = q));
        setResult({
          percent: Math.round(Number(r.scorePercent) || 0),
          passed: !!r.passed,
          timedOut: !!r.timedOut,
          marksScored: r.marksScored,
          totalMarks: r.totalMarks,
          attemptNumber: r.attemptNumber ?? attempt?.attemptNumber,
          maxAttempts: r.maxAttempts ?? maxAttempts,
          attemptsRemaining: r.attemptsRemaining,
          timeTakenSeconds: r.timeTakenSeconds,
          answersRevealed: r.answersRevealed !== false,
          completedAt,
          feedback: (r.feedback || []).map((f) => ({
            questionId: f.questionId,
            correct: !!f.isCorrect,
            questionText: f.questionText || byId[f.questionId]?.text || "",
            explanation: f.explanation || "",
            hints: f.hints || [],
            marksAwarded: f.marksAwarded,
            selectedOptionIds: normalizeIds(f.selectedOptionIds),
            correctOptionIds: normalizeIds(f.correctOptionIds),
            options: (f.options || byId[f.questionId]?.options || []).map((option, optionIndex) => {
              const id = String(option?._id || option?.id || option?.optionId || optionIndex);
              const selectedIds = normalizeIds(f.selectedOptionIds);
              const correctIds = normalizeIds(f.correctOptionIds);
              const isSelected = option?.isSelected ?? selectedIds.includes(id);
              const isCorrect = option?.isCorrect ?? (
                correctIds.length ? correctIds.includes(id) : (!!f.isCorrect && selectedIds.includes(id))
              );
              return {
                id,
                text: option?.text || option?.optionText || option?.label || "",
                isSelected,
                isCorrect,
                explanation:
                  option?.explanation ||
                  option?.hint ||
                  (isSelected && !isCorrect ? (f.hints || [])[0] || "" : ""),
              };
            }),
          })),
        });
        if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
      } else {
        if (requiresRemainingAnswers(message)) {
          setSubmitWarning(
            t(
              "learner.lms.quiz.answerRequiredMessage",
              "Please answer the remaining required question before submitting. Review the highlighted question and submit again."
            )
          );
          const firstUnanswered = questions.findIndex((question) => !(answers[question.id] || []).length);
          if (firstUnanswered >= 0) goToQuestion(firstUnanswered);
          if (secondsLeft > 0) startTimer(Date.now() + secondsLeft * 1000);
        } else {
          setBlocked(message || t("common.somethingWentWrong"));
        }
      }
    } catch (e) {
      const apiMessage = e?.response?.data?.message;
      if (requiresRemainingAnswers(apiMessage)) {
        setSubmitWarning(
          t(
            "learner.lms.quiz.answerRequiredMessage",
            "Please answer the remaining required question before submitting. Review the highlighted question and submit again."
          )
        );
        const firstUnanswered = questions.findIndex((question) => !(answers[question.id] || []).length);
        if (firstUnanswered >= 0) goToQuestion(firstUnanswered);
        if (secondsLeft > 0) startTimer(Date.now() + secondsLeft * 1000);
      } else {
        globalAlert("error", apiMessage);
        if (secondsLeft > 0) startTimer(Date.now() + secondsLeft * 1000);
      }
    }
    setSubmitting(false);
    submittingRef.current = false;
  };
  submitRef.current = submit;

  const exit = async () => {
    if (!result && Object.keys(answers).length) {
      const ok = window.confirm(
        t("learner.lms.quiz.confirmExit", "Leave the quiz? Your saved progress is kept.")
      );
      if (!ok) return;
    }
    if (!result && attemptRef.current?.attemptId) await saveDraftNow();
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    backToCourse();
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    else document.documentElement.requestFullscreen?.().catch(() => {});
  };

  /* ── derived ── */
  const total = questions.length;
  const q = questions[idx];
  const goToQuestion = (nextIndex) => {
    const safeIndex = Math.min(Math.max(Number(nextIndex) || 0, 0), Math.max(total - 1, 0));
    setIdx(safeIndex);
    setVisitedQuestions((prev) => new Set([...prev, safeIndex]));
  };
  const answeredCount = useMemo(
    () => questions.filter((qq) => (answers[qq.id] || []).length).length,
    [questions, answers]
  );
  const notAnsweredCount = useMemo(
    () =>
      questions.filter((qq, questionIndex) => {
        return visitedQuestions.has(questionIndex) && !(answers[qq.id] || []).length;
      }).length,
    [questions, answers, visitedQuestions]
  );
  const notVisitedCount = Math.max(0, total - visitedQuestions.size);
  const attemptsRemaining =
    result?.attemptsRemaining != null
      ? result.attemptsRemaining
      : result?.attemptNumber != null && maxAttempts != null
      ? Math.max(0, maxAttempts - result.attemptNumber)
      : maxAttempts != null && attempt?.attemptNumber != null
      ? Math.max(0, maxAttempts - attempt.attemptNumber + 1)
      : null;

  const title = nav.title || attempt?.title || t("learner.lms.player.quiz", "Quiz");
  const subtitle = nav.subtitle || "";
  const configuredTimeLimit = attempt?.timeLimitMinutes ?? nav.timeLimitMinutes ?? null;
  const configuredQuestionCount = questions.length || nav.questionCount || 0;

  /* ── render ── */
  return (
    <>
      <Helmet>
        <title>{title} - {brandName}</title>
      </Helmet>
      <div className="lms-assess">
        <header className="lms-assess-top">
          <div className="lms-assess-brand">
            <img
              src={brandLogo}
              alt=""
              className="lms-assess-logo"
              onError={(event) => {
                if (event.currentTarget.src !== window.location.origin + fallbackBrandLogo) {
                  event.currentTarget.src = fallbackBrandLogo;
                }
              }}
            />
            <span className="lms-assess-portal">{brandName}</span>
            <span className="lms-assess-divider" aria-hidden="true"></span>
            <span className="lms-assess-mode">{t("learner.lms.quiz.assessmentMode", "Assessment Mode")}</span>
          </div>
          <div className="lms-assess-toolbar">
            {remaining != null && !result && (
              <span className={`lms-assess-timer ${remaining < 60 ? "danger" : ""}`}>
                <i className="far fa-clock mr-1"></i>
                {fmtClock(remaining)}
              </span>
            )}
            {attemptsRemaining != null && (
              <span className="lms-assess-attempts">
                {t("learner.lms.quiz.attemptsRemaining", "Attempts Remaining")}:{" "}
                <strong>{attemptsRemaining}/{maxAttempts}</strong>
              </span>
            )}
            {!result && attempt?.attemptId && (
              <span className={`lms-assess-draft-state ${draftState}`} role="status">
                {draftState === "saving"
                  ? t("learner.lms.quiz.savingDraft", "Saving…")
                  : draftState === "error"
                  ? t("learner.lms.quiz.draftSaveFailed", "Save failed")
                  : t("learner.lms.quiz.draftSaved", "Saved")}
              </span>
            )}
            <button className="lms-assess-iconbtn" onClick={toggleFullscreen} aria-label={t("learner.lms.quiz.fullscreen", "Fullscreen")}>
              <i className={`fas ${isFs ? "fa-compress" : "fa-expand"}`}></i>
            </button>
            <button className="lms-assess-exit" onClick={exit}>
              <i className="fas fa-times me-2"></i>
              {t("learner.lms.quiz.exit", "Exit")}
            </button>
          </div>
        </header>

        {/* Progress */}
        <div className="lms-assess-progressbar" aria-hidden="true">
          <span style={{ width: `${total ? (answeredCount / total) * 100 : 0}%` }}></span>
        </div>

        <div className="lms-assess-body">
          <main className="lms-assess-main">
            <div className="lms-assess-heading">
              <h1 className="lms-assess-title">
                {t("learner.lms.quiz.quizLabel", "Quiz")}: {title}
              </h1>
              {subtitle && <p className="lms-assess-subtitle">{subtitle}</p>}
              {!loading && !blocked && (
                <div className="lms-quiz-meta">
                  {configuredQuestionCount > 0 && (
                    <span className="lms-quiz-chip">
                      {configuredQuestionCount === 1
                        ? t("learner.lms.quiz.questionCountOne", "{{count}} Question", {
                          count: configuredQuestionCount,
                        })
                        : t("learner.lms.quiz.questionCount", "{{count}} Questions", {
                          count: configuredQuestionCount,
                        })}
                    </span>
                  )}
                  <span className="lms-quiz-chip">
                    {t("learner.lms.quiz.passMark", "Pass mark")} {passMark}%
                  </span>
                  <span className={`lms-quiz-chip ${configuredTimeLimit ? "" : "danger"}`}>
                    {configuredTimeLimit
                      ? `${t("learner.lms.quiz.timeLimit", "Time limit")} ${configuredTimeLimit} ${t("learner.lms.quiz.minutesShort", "min")}`
                      : t("learner.lms.quiz.noTimeLimit", "No time limit")}
                  </span>
                </div>
              )}
            </div>

            {loading && (
              <div className="lms-assess-loading">
                <i className="fas fa-circle-notch fa-spin mr-2"></i>
                {t("common.loading", "Loading…")}
              </div>
            )}

            {!loading && blocked && (
              <div className="lms-assess-blocked" role="alert">
                <div className="lms-assess-blocked-icon">
                  <i className="fas fa-lock"></i>
                </div>
                <div className="lms-assess-blocked-copy">
                  <h2>{t("learner.lms.quiz.unavailableTitle", "Quiz is not available")}</h2>
                  <p>{blocked || t("learner.lms.quiz.unavailable", "This quiz is not available right now.")}</p>
                </div>
                <div className="lms-assess-blocked-actions">
                  <button className="btn lms-btn-primary" onClick={backToCourse}>
                    {t("learner.lms.quiz.backToCourse", "Back to course")}
                  </button>
                </div>
              </div>
            )}

            {/* Result */}
            {!loading && result && (
              // <>
              // <div className="lms-assess-result">
              //   <div className={`lms-assess-score ${result.passed ? "pass" : "fail"}`}>
              //     <div className="lms-assess-score-num">{result.percent}%</div>
              //     <div className="lms-assess-score-label">
              //       <strong>
              //         {result.passed
              //           ? t("learner.lms.quiz.passed", "Passed")
              //           : result.timedOut
              //           ? t("learner.lms.quiz.timedOut", "Time ran out")
              //           : t("learner.lms.quiz.failed", "Not passed")}
              //       </strong>
              //       <span>
              //         {t("learner.lms.quiz.passMark", "Pass mark")} {passMark}%
              //         {result.marksScored != null && result.totalMarks != null
              //           ? ` · ${result.marksScored}/${result.totalMarks}`
              //           : ""}
              //       </span>
              //     </div>
              //   </div>

              //   <div className="lms-assess-feedback">
              //     {result.feedback.map((f, i) => (
              //       <div className={`lms-assess-fb ${f.correct ? "ok" : "bad"}`} key={i}>
              //         <i className={`fas ${f.correct ? "fa-check-circle" : "fa-times-circle"} mr-2`}></i>
              //         <div>
              //           <div className="lms-assess-fb-q">{i + 1}. {f.questionText}</div>
              //           {!f.correct && (f.hints || []).map((h, hi) => (
              //             <div className="lms-assess-fb-hint" key={hi}>
              //               <i className="fas fa-lightbulb mr-1"></i>{h}
              //             </div>
              //           ))}
              //           {!f.correct && f.explanation && (
              //             <div className="lms-assess-fb-exp">{f.explanation}</div>
              //           )}
              //         </div>
              //       </div>
              //     ))}
              //   </div>

              //   <div className="lms-assess-result-actions">
              //     {!result.passed &&
              //       (attemptsRemaining == null || attemptsRemaining > 1) && (
              //         <button className="btn lms-btn-ghost" onClick={start}>
              //           <i className="fas fa-redo me-2"></i>
              //           {t("learner.lms.quiz.retry", "Try again")}
              //         </button>
              //       )}
              //     <button className="btn lms-btn-primary" onClick={backToCourse}>
              //       {t("learner.lms.quiz.backToCourse", "Back to course")}
              //     </button>
              //   </div>
              // </div>
              // </>
              <>
              <LmsQuizResultSummary
  result={result}
  passMark={passMark}
  previewResult={false}
  scorePercent={result.percent}
  retake={start}
  loading={loading}
  attemptsRemaining={attemptsRemaining}
  attemptNumber={result.attemptNumber}
  maxAttempts={result.maxAttempts ?? maxAttempts}
/>
        </>
            )}
            {/* Active question */}
            {!loading && !blocked && !result && q && (
              <>
                {submitWarning && (
                  <div className="lms-assess-warning" role="alert">
                    <div className="lms-assess-warning-icon">
                      <i className="fas fa-exclamation-circle"></i>
                    </div>
                    <div className="lms-assess-warning-copy">
                      <h2>{t("learner.lms.quiz.answerRequiredTitle", "Answer required")}</h2>
                      <p>{submitWarning}</p>
                    </div>
                    <button
                      type="button"
                      className="btn lms-btn-primary"
                      onClick={() => {
                        const firstUnanswered = questions.findIndex(
                          (question) => !(answers[question.id] || []).length
                        );
                        if (firstUnanswered >= 0) goToQuestion(firstUnanswered);
                      }}
                    >
                      {t("learner.lms.quiz.reviewUnanswered", "Review unanswered")}
                    </button>
                  </div>
                )}

                <div className="lms-assess-qcard">
                  <span className="lms-assess-qnum">{idx + 1}</span>
                  <div className="lms-assess-qbody">
                    <h2 className="lms-assess-qtext">{q.text}</h2>
                    <p className="lms-assess-qhint">
                      {q.multi
                        ? t("learner.lms.quiz.selectAll", "Select all that apply.")
                        : t("learner.lms.quiz.selectOne", "Select one answer.")}
                    </p>
                    <div className="lms-assess-options">
                      {q.options.map((o) => {
                        const sel = (answers[q.id] || []).includes(o.id);
                        return (
                          <label key={o.id} className={`lms-assess-option ${sel ? "selected" : ""}`}>
                            <input
                              type={q.multi ? "checkbox" : "radio"}
                              name={`q-${q.id}`}
                              checked={sel}
                              onChange={q.multi ? () => toggle(q, o.id) : undefined}
                              onClick={!q.multi ? () => toggle(q, o.id) : undefined}
                            />
                            <span className="lms-assess-option-mark" aria-hidden="true"></span>
                            <span className="lms-assess-option-text">{o.text}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Bottom bar */}
                <div className="lms-assess-actions">
                  <button
                    className="btn lms-btn-primary"
                    disabled={idx === 0}
                    onClick={() => goToQuestion(idx - 1)}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    {t("learner.lms.player.previous", "Previous")}
                  </button>
                  <div className="lms-assess-actions-right">
                    {idx < total - 1 ? (
                      <button
                        className="btn lms-btn-primary"
                        onClick={() => goToQuestion(idx + 1)}
                      >
                        {t("learner.lms.player.next", "Next")}
                        <i className="fas fa-arrow-right ms-2"></i>
                      </button>
                    ) : (
                      <button className="btn lms-btn-ghost" onClick={() => submit(false)} disabled={submitting}>
                        {submitting ? (
                          <i className="fas fa-circle-notch fa-spin"></i>
                        ) : (
                          <>
                            {t("learner.lms.quiz.submitQuiz", "Submit Quiz")}
                            <i className="fas fa-paper-plane ms-2"></i>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </main>

          {/* Navigator */}
          {!loading && !blocked && !result && total > 0 && (
                <aside
                  className="lms-assess-nav"
                  aria-label={t("learner.lms.quiz.navigator", "Question Navigator")}
                >
                  <h3 className="lms-assess-nav-title">
                    {t("learner.lms.quiz.navigator", "Question Navigator")}
                  </h3>

                  {/* Attempts */}
                  {maxAttempts != null && <div className="lms-assess-attempts-card">
                    <div className="lms-assess-attempts-icon">
                      <i className="fas fa-bullseye"></i>
                    </div>

                    <div className="lms-assess-attempts-content">
                      <div className="lms-assess-attempts-label">
                        {t("learner.lms.quiz.attemptsRemaining", "Attempts Remaining")}
                      </div>

                      <div className="lms-assess-attempts-count">
                        {attemptsRemaining}
                      </div>

                      <small>
                        {t(
                          "learner.lms.quiz.attemptHint",
                          "Total attempts : {{count}}",
                          { count:maxAttempts}
                        )}
                      </small>
                    </div>
                  </div>}

                  {/* Question Navigator */}
                  <div className="lms-assess-nav-grid">
                    {questions.map((qq, i) => {
                      const done = (answers[qq.id] || []).length > 0;
                      const visited = visitedQuestions.has(i);
                      const notAnswered = visited && !done;
                      const notVisited = !visited;
                      // The cell's status (done/not-answered/not-visited) was only
                      // conveyed by background color (WCAG 1.4.1) — spell it out in
                      // the accessible name too.
                      const statusLabel = done
                        ? t("learner.lms.quiz.completed", "Completed")
                        : notAnswered
                        ? t("learner.lms.quiz.notAnswered", "Not Answered")
                        : t("learner.lms.quiz.notVisited", "Not Visited");

                      return (
                        <button
                          key={qq.id}
                          className={`lms-assess-nav-cell ${
                            i === idx ? "current" : ""
                          } ${done ? "done" : ""} ${notAnswered ? "not-answered" : ""} ${
                            notVisited ? "not-visited" : ""
                          }`}
                          onClick={() => goToQuestion(i)}
                          aria-current={i === idx ? "true" : undefined}
                          aria-label={`${t("learner.lms.quiz.questionNumberLabel", "Question {{n}}", { n: i + 1 })}, ${statusLabel}`}
                        >
                          {i + 1}
                        </button>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="lms-assess-nav-legend">
                    <span>
                      <i className="lms-assess-dot done"></i>
                      {t("learner.lms.quiz.completed", "Completed")} ({answeredCount})
                    </span>

                    <span>
                      <i className="lms-assess-dot not-answered"></i>
                      {t("learner.lms.quiz.notAnswered", "Not Answered")} ({notAnsweredCount})
                    </span>

                    <span>
                      <i className="lms-assess-dot not-visited"></i>
                      {t("learner.lms.quiz.notVisited", "Not Visited")} ({notVisitedCount})
                    </span>
                  </div>

                  {/* Pass mark */}
                  <div className="lms-assess-nav-info">
                    <i className="fas fa-award"></i>
                    <span>
                      {t("learner.lms.quiz.passMark", "Pass mark")}:
                      <strong> {passMark}%</strong>
                    </span>
                  </div>
                </aside>
              )}
        </div>

      </div>
    </>
  );
};

export default LearnerQuiz;
